import { formatRelative } from "@/lib/utils";
import type { SubmissionStatusHistoryEntry } from "./types";

export function StatusHistoryTimeline({
  entries,
  decidedByName,
  decisionNote,
}: {
  entries: SubmissionStatusHistoryEntry[];
  decidedByName: string | null;
  decisionNote: string | null;
}) {
  if (!entries || entries.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">No activity recorded.</p>
    );
  }

  // Sort oldest → newest so the timeline reads top-down.
  const sorted = [...entries].sort((a, b) => {
    const at = a.changed_at ?? "";
    const bt = b.changed_at ?? "";
    return at.localeCompare(bt);
  });

  return (
    <ol className="space-y-3 border-l border-border/70 pl-4">
      {sorted.map((entry, idx) => {
        const isFinal = idx === sorted.length - 1;
        const label = entry.from
          ? `${niceStatus(entry.from)} → ${niceStatus(entry.to)}`
          : niceStatus(entry.status);
        const byLabel =
          isFinal && decidedByName
            ? decidedByName
            : entry.changed_by === "system"
              ? "System"
              : entry.changed_by
                ? entry.changed_by.slice(0, 8)
                : "Unknown";
        return (
          <li key={idx} className="relative">
            <span className="absolute -left-[21px] top-1.5 inline-flex h-2 w-2 rounded-full bg-foreground/40" />
            <div className="text-sm text-foreground">{label}</div>
            <div className="text-xs text-muted-foreground">
              {entry.changed_at ? formatRelative(entry.changed_at) : "—"}
              {" · "}
              {byLabel}
            </div>
            {isFinal && decisionNote ? (
              <div className="mt-1 rounded-md border bg-muted/30 p-2 text-xs italic text-muted-foreground">
                &ldquo;{decisionNote}&rdquo;
              </div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function niceStatus(s: string | undefined): string {
  if (!s) return "—";
  switch (s) {
    case "pending":
      return "Pending";
    case "accepted":
      return "Accepted";
    case "declined":
      return "Declined";
    default:
      return s;
  }
}
